import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import ComboFilter from "../components/ComboFilter";

interface BudgetRow {
  name: string;
  id: string;
  audiences: string | null;
  audience_ids: string | null;
  max_date: string;
  apro_revenue_eur: number;
  conversion_cpx: number | null;
  target_roas: number | null;
}

interface D1Metrics {
  revenue_eur: number;
  prev_revenue_eur: number;
  d1_ecpx_eur: number | null;
  prev_d1_ecpx_eur: number | null;
  d1_roas: number | null;
  prev_d1_roas: number | null;
}

interface CohortedBudgetRow {
  budget_id: string;
  curr_revenue_eur: number;
  prev_revenue_eur: number;
  curr_d1_conv: number;
  prev_d1_conv: number;
  curr_ap_rev: number;
  prev_ap_rev: number;
}

const formatEUR = (value: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

const formatEURCpx = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

function formatChange(current: number, prev: number): { label: string; up: boolean } | null {
  if (!prev || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatROAS(value: number): string {
  return `${Math.round(value * 100)}%`;
}

// For eCPX: lower is better, so a decrease is "up" (positive)
function formatChangeCpx(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct <= 0 };
}

// For ROAS: higher is better
function formatChangeRoas(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatDateRange(maxDateStr: string): string {
  const end = new Date(maxDateStr);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

interface ManagerRow {
  budget_pool_id: string;
  account_manager: string | null;
  adops_manager: string | null;
}

const MANAGER_SQL = `
  SELECT DISTINCT
    CAST(c.budget_pool_id AS STRING) AS budget_pool_id,
    am.name AS account_manager,
    adops.name AS adops_manager
  FROM \`martin-test-datalab.postgres.campaigns\` c
  JOIN \`martin-test-datalab.postgres.organizations\` o ON c.organization_id = o.id
  LEFT JOIN \`martin-test-datalab.postgres.users\` am ON o.account_manager_id = am.id
  LEFT JOIN \`martin-test-datalab.postgres.users\` adops ON o.adops_manager_id = adops.id
  WHERE c.budget_pool_id IS NOT NULL
`;

const BUDGET_SQL = `
  WITH latest AS (
    SELECT MAX(day) AS max_date
    FROM \`remerge-dwh.ops.apro_campaign_level_performance\`
    WHERE day >= DATE_SUB(CURRENT_DATE(), INTERVAL 30 DAY)
      AND day <= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)
  ),
  budget_revenue AS (
    SELECT
      budget_id,
      SUM(revenue_eur) AS revenue_eur
    FROM \`remerge-dwh.ops.apro_campaign_level_performance\`
    CROSS JOIN latest l
    WHERE day >= DATE_SUB(l.max_date, INTERVAL 6 DAY)
    GROUP BY budget_id
  ),
  budget_audiences AS (
    SELECT
      c.budget_pool_id,
      STRING_AGG(DISTINCT a.name, ', ') AS audiences,
      STRING_AGG(DISTINCT CAST(a.id AS STRING), ', ') AS audience_ids
    FROM \`martin-test-datalab.postgres.campaigns\` c
    JOIN \`martin-test-datalab.postgres.audiences\` a ON c.audience_id = a.id
    WHERE c.budget_pool_id IN (SELECT budget_id FROM budget_revenue)
    GROUP BY c.budget_pool_id
  )
  SELECT
    CAST(bp.id AS STRING) AS id,
    bp.name,
    l.max_date,
    br.revenue_eur AS apro_revenue_eur,
    ba.audiences,
    ba.audience_ids,
    bp.conversion_cpx,
    bp.target_roas
  FROM budget_revenue br
  JOIN \`martin-test-datalab.postgres.budget_pools\` bp ON br.budget_id = bp.id
  CROSS JOIN latest l
  LEFT JOIN budget_audiences ba ON br.budget_id = ba.budget_pool_id
  WHERE bp.name NOT LIKE '%(migrated)%'
  ORDER BY br.revenue_eur DESC
`;

function buildCohortedD1SQL(maxDate: string): string {
  const split = addDays(maxDate, -6);
  const start = addDays(maxDate, -13);
  return `
    SELECT
      CAST(budget_id AS STRING) AS budget_id,
      SUM(CASE WHEN bid_date >= '${split}' THEN revenue_eur ELSE 0 END) AS curr_revenue_eur,
      SUM(CASE WHEN bid_date < '${split}' THEN revenue_eur ELSE 0 END) AS prev_revenue_eur,
      SUM(CASE WHEN bid_date >= '${split}' THEN d1_ap_conversions ELSE 0 END) AS curr_d1_conv,
      SUM(CASE WHEN bid_date < '${split}' THEN d1_ap_conversions ELSE 0 END) AS prev_d1_conv,
      SUM(CASE WHEN bid_date >= '${split}' THEN d1_ap_customer_revenue_eur ELSE 0 END) AS curr_ap_rev,
      SUM(CASE WHEN bid_date < '${split}' THEN d1_ap_customer_revenue_eur ELSE 0 END) AS prev_ap_rev
    FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\`
    WHERE bid_date >= '${start}' AND bid_date <= '${maxDate}'
    GROUP BY budget_id
  `;
}

function computeD1ByBudget(rows: CohortedBudgetRow[]): Map<string, D1Metrics> {
  const result = new Map<string, D1Metrics>();
  for (const row of rows) {
    result.set(row.budget_id, {
      revenue_eur: row.curr_revenue_eur,
      prev_revenue_eur: row.prev_revenue_eur,
      d1_ecpx_eur: row.curr_d1_conv > 0 ? row.curr_revenue_eur / row.curr_d1_conv : null,
      prev_d1_ecpx_eur: row.prev_d1_conv > 0 ? row.prev_revenue_eur / row.prev_d1_conv : null,
      d1_roas: row.curr_revenue_eur > 0 ? row.curr_ap_rev / row.curr_revenue_eur : null,
      prev_d1_roas: row.prev_revenue_eur > 0 ? row.prev_ap_rev / row.prev_revenue_eur : null,
    });
  }
  return result;
}

export default function BudgetListPage() {
  const { isAuthenticated, requiresAuth } = useAuth();
  const [rows, setRows] = useState<BudgetRow[]>([]);
  const [d1Metrics, setD1Metrics] = useState<Map<string, D1Metrics>>(new Map());
  const [managerMap, setManagerMap] = useState<Map<string, ManagerRow>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [amFilter, setAmFilter] = useState("");
  const [adopsFilter, setAdopsFilter] = useState("");

  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    Promise.all([runBigQueryQuery(BUDGET_SQL), runBigQueryQuery(MANAGER_SQL)])
      .then(([budgetResults, managerResults]) => {
        const budgetRows = budgetResults as unknown as BudgetRow[];
        setRows(budgetRows);
        const mgrs = new Map<string, ManagerRow>();
        for (const row of managerResults as unknown as ManagerRow[]) {
          mgrs.set(row.budget_pool_id, row);
        }
        setManagerMap(mgrs);
        setIsLoading(false);

        const maxDate = budgetRows[0]?.max_date;
        if (!maxDate) return;

        return runBigQueryQuery(buildCohortedD1SQL(maxDate))
          .then(d1Results => setD1Metrics(computeD1ByBudget(d1Results as unknown as CohortedBudgetRow[])))
          .catch(err => setError(String(err)));
      })
      .catch(err => {
        setError(String(err));
        setIsLoading(false);
      });
  }, [isAuthenticated, requiresAuth]);

  if (isLoading) {
    return <div className="entity-list__loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading budgets. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );
  }

  const dateRange = rows[0]?.max_date ? formatDateRange(rows[0].max_date) : null;
  const filterLower = filter.toLowerCase();

  const activeIds = new Set(rows.map(r => r.id));
  const activeManagers = [...managerMap.entries()].filter(([id]) => activeIds.has(id)).map(([, m]) => m);
  const amNames = [...new Set(activeManagers.map(m => m.account_manager).filter(Boolean) as string[])].sort();
  const adopsNames = [...new Set(activeManagers.map(m => m.adops_manager).filter(Boolean) as string[])].sort();

  const visibleRows = rows.filter(r => {
    if (
      filterLower &&
      !r.name.toLowerCase().includes(filterLower) &&
      !(r.audiences ?? "").toLowerCase().includes(filterLower)
    )
      return false;
    if (amFilter || adopsFilter) {
      const mgr = managerMap.get(r.id);
      if (amFilter && mgr?.account_manager !== amFilter) return false;
      if (adopsFilter && mgr?.adops_manager !== adopsFilter) return false;
    }
    return true;
  });

  return (
    <div className="entity-list">
      <table className="entity-list__table">
        <caption className="entity-list__table-caption">
          <div className="entity-list__table-caption-inner">
            <div>
              <h1 className="entity-list__title">Budgets</h1>
              <p className="entity-list__subtitle">Sorted by revenue{dateRange ? ` — ${dateRange}` : ""}</p>
            </div>
            <div className="entity-list__filters">
              <input
                className="entity-list__filter"
                type="text"
                placeholder="Filter by budget or audience…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
              <ComboFilter placeholder="Account Manager…" options={amNames} value={amFilter} onChange={setAmFilter} />
              <ComboFilter placeholder="AdOps…" options={adopsNames} value={adopsFilter} onChange={setAdopsFilter} />
            </div>
          </div>
        </caption>
        <thead>
          <tr>
            <th>Budget</th>
            <th>Audience</th>
            <th className="entity-list__col--right">D1 AP eCPX</th>
            <th className="entity-list__col--right">D1 AP ROAS</th>
            <th className="entity-list__col--right">Revenue (EUR)</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(row => {
            const d1 = d1Metrics.get(row.id);
            const change = d1 ? formatChange(d1.revenue_eur, d1.prev_revenue_eur) : null;
            const hasCpxTarget = row.conversion_cpx != null && row.conversion_cpx > 0;
            const hasRoasTarget = row.target_roas != null && row.target_roas > 0;
            const cpxDimmed = hasRoasTarget && !hasCpxTarget;
            const roasDimmed = hasCpxTarget && !hasRoasTarget;
            const cpxChange = formatChangeCpx(d1?.d1_ecpx_eur ?? null, d1?.prev_d1_ecpx_eur ?? null);
            const roasChange = formatChangeRoas(d1?.d1_roas ?? null, d1?.prev_d1_roas ?? null);
            return (
              <tr key={row.id} className="entity-list__row">
                <td className="entity-list__col--name" title={row.id}>
                  <Link to={`/budgets/${row.id}`} className="entity-list__link">
                    {row.name}
                  </Link>
                </td>
                <td className="entity-list__col--audiences" title={row.audience_ids ?? ""}>
                  {row.audiences ?? "–"}
                </td>
                <td
                  className={`entity-list__col--right entity-list__col--revenue${cpxDimmed ? " entity-list__col--dimmed" : ""}`}
                >
                  {d1?.d1_ecpx_eur != null ? formatEURCpx(d1.d1_ecpx_eur) : "–"}
                  {cpxChange && (
                    <span className={`entity-list__change entity-list__change--${cpxChange.up ? "up" : "down"}`}>
                      {" "}
                      ({cpxChange.label})
                    </span>
                  )}
                </td>
                <td
                  className={`entity-list__col--right entity-list__col--revenue${roasDimmed ? " entity-list__col--dimmed" : ""}`}
                >
                  {d1?.d1_roas != null ? formatROAS(d1.d1_roas) : "–"}
                  {roasChange && (
                    <span className={`entity-list__change entity-list__change--${roasChange.up ? "up" : "down"}`}>
                      {" "}
                      ({roasChange.label})
                    </span>
                  )}
                </td>
                <td className="entity-list__col--right entity-list__col--revenue">
                  {d1 ? formatEUR(d1.revenue_eur) : "–"}
                  {change && (
                    <span className={`entity-list__change entity-list__change--${change.up ? "up" : "down"}`}>
                      {" "}
                      ({change.label})
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="entity-list__empty">
                No budgets found for the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
